// src/components/Training.js
import React, { useState } from 'react';
import { Card, Accordion, Button, Badge } from 'react-bootstrap';

export default function Training() {
  const [activeKey, setActiveKey] = useState('0');

  const trainingGuides = [
    {
      id: '0',
      title: 'My Diary - Complete Guide',
      icon: '📅',
      steps: [
        {
          step: 1,
          title: 'Understanding My Diary Page',
          description: 'My Diary is your central hub for managing all your taxi jobs.',
          details: [
            '📋 Job Management: View, edit, and manage all your accepted jobs',
            '📊 Financial Summary: Track your earnings with detailed breakdowns',
            '📱 Real-time Updates: See job status changes instantly',
            '🔍 Search & Filter: Find specific jobs quickly',
            '📈 Performance Tracking: Monitor your business performance'
          ]
        },
        {
          step: 2,
          title: 'Navigation Tabs',
          description: 'My Diary has three main sections to organize your jobs:',
          details: [
            '📅 Accepted Jobs: Current and upcoming jobs you\'ve accepted',
            '⏳ Uncompleted Jobs: Jobs that are finished but not yet marked complete',
            '✅ Completed Jobs: Historical record of all finished jobs'
          ]
        },
        {
          step: 3,
          title: 'Job Card Features - What You Can Click',
          description: 'Each job card has multiple interactive elements:',
          details: [
            '📝 Job Details: Click anywhere on the job card to expand/collapse details',
            '✏️ Edit Button: Modify job information (customer, times, locations)',
            '📧 Email Button: Send confirmation emails to customers',
            '📞 Call Button: Direct phone call to customer',
            '🗺️ Map Button: Open location in maps app',
            '💰 Fare Button: Set and update fare amounts',
            '📄 Invoice Button: Generate and send invoices',
            '🔄 Re-assign Button: Transfer job to another driver',
            '✅ Complete Button: Mark job as finished'
          ]
        },
        {
          step: 4,
          title: 'Repeat Job Buttons',
          description: 'Quickly duplicate jobs for recurring customers:',
          details: [
            '⏰ +Tomorrow: Copy job to tomorrow at same time',
            '📅 +Next Week: Copy job to same day next week',
            '📆 +Next Month: Copy job to same date next month',
            '⚙️ Wait & Return: All settings including wait times are copied',
            '✏️ Edit Before Save: Modify any details before creating the duplicate'
          ]
        },
        {
          step: 5,
          title: 'Financial Summary Tab',
          description: 'Track your earnings with comprehensive financial data:',
          details: [
            '💰 Today\'s Earnings: Current day totals with breakdown',
            '📊 Weekly Summary: This week\'s earnings by day',
            '📈 Monthly Reports: Complete monthly performance',
            '🔍 Drill-Down Analysis: Click any amount to see detailed breakdown',
            '📄 Export Reports: Download financial reports for records'
          ]
        },
        {
          step: 6,
          title: 'Job Status Indicators',
          description: 'Visual indicators help you understand job status at a glance:',
          details: [
            '🟢 Green Border: Normal accepted jobs',
            '🔵 Blue Flashing: New booking link jobs (direct customer bookings)',
            '🟡 Yellow: Jobs requiring attention or updates',
            '🔴 Red: Overdue or problem jobs',
            '⏰ Clock Icon: Wait and return jobs with time display',
            '🐕 Dog Icon: Pet-friendly jobs',
            '♿ Wheelchair Icon: Disabled access required'
          ]
        },
        {
          step: 7,
          title: 'Search and Filter Options',
          description: 'Find specific jobs quickly with powerful search tools:',
          details: [
            '🔍 Search Bar: Search by customer name, location, or notes',
            '📅 Date Filters: Filter by specific dates or date ranges',
            '💰 Fare Filters: Show jobs by fare amount ranges',
            '📊 Status Filters: Filter by job status (pending, completed, etc.)',
            '📍 Location Filters: Find jobs by pickup or destination area'
          ]
        },
        {
          step: 8,
          title: 'Mobile Optimization',
          description: 'My Diary is fully optimized for mobile devices:',
          details: [
            '📱 Touch-Friendly: Large buttons and easy finger navigation',
            '🔄 Swipe Gestures: Swipe to reveal additional options',
            '📲 Push Notifications: Get alerts for job updates',
            '🌐 Offline Access: View jobs even without internet',
            '📞 One-Tap Calling: Direct phone calls to customers'
          ]
        }
      ]
    },
    {
      id: '2',
      title: 'Adding a Job',
      icon: '📝',
      steps: [
        {
          step: 1,
          title: 'Navigate to My Diary',
          description: 'Click on "My Diary" in the main navigation menu.',
          details: 'This will take you to your accepted jobs page where you can manage your current bookings.'
        },
        {
          step: 2,
          title: 'Click "Add to Diary" Button',
          description: 'Look for the green "➕" button in the top right corner of the page.',
          details: 'This button allows you to manually add a new job to your diary.'
        },
        {
          step: 3,
          title: 'Fill in Job Details',
          description: 'Complete the form with the following information:',
          details: [
            'Customer Name: Enter the passenger\'s full name',
            'Email: Customer\'s email address',
            'Phone: Customer\'s contact number',
            'From: Pickup location',
            'To: Destination',
            'Date & Time: When the journey is scheduled',
            'Passengers: Number of people traveling',
            'Return Trip: Check if customer needs return journey',
            'Special Requirements: Dog/Pet, Disabled Access, Notes'
          ]
        },
        {
          step: 4,
          title: 'Save the Job',
          description: 'Click "Add to Diary" to save the job.',
          details: 'The job will now appear in your diary and you can manage it like any other accepted job.'
        }
      ]
    },
    {
      id: '3',
      title: 'Creating a Return Trip',
      icon: '🔄',
      steps: [
        {
          step: 1,
          title: 'Select the Original Job',
          description: 'Go to "My Diary" and find the job you want to create a return trip for.',
          details: 'Look for the job in your current diary entries.'
        },
        {
          step: 2,
          title: 'Click "Return Trip"',
          description: 'Find the "Return Trip" button or option for that specific job.',
          details: 'This will create a new job entry with the reverse journey details.'
        },
        {
          step: 3,
          title: 'Review Return Details',
          description: 'The system will automatically swap the "From" and "To" locations.',
          details: 'Verify the return date/time and adjust if needed. The return trip will typically be scheduled after the original journey.'
        },
        {
          step: 4,
          title: 'Confirm Return Trip',
          description: 'Click "Create Return Trip" to add it to your diary.',
          details: 'Both the original and return trips will now be in your diary for management.'
        }
      ]
    },
    {
      id: '4',
      title: 'Creating a Recurring Trip',
      icon: '📅',
      steps: [
        {
          step: 1,
          title: 'Add Initial Job',
          description: 'First, add the job as you normally would using "Add to Diary".',
          details: 'This creates the template for your recurring trips.'
        },
        {
          step: 2,
          title: 'Access Recurring Options',
          description: 'After adding the job, look for "Make Recurring" or similar option.',
          details: 'This option allows you to set up repeat bookings.'
        },
        {
          step: 3,
          title: 'Set Recurrence Pattern',
          description: 'Choose how often the trip should repeat:',
          details: [
            'Daily: Every day at the same time',
            'Weekly: Same day each week',
            'Monthly: Same date each month',
            'Custom: Specific days of the week'
          ]
        },
        {
          step: 4,
          title: 'Set End Date',
          description: 'Choose when the recurring trips should stop.',
          details: 'You can set a specific end date or number of occurrences.'
        },
        {
          step: 5,
          title: 'Confirm Recurring Setup',
          description: 'Review all details and click "Create Recurring Trips".',
          details: 'The system will create multiple job entries in your diary for the specified period.'
        }
      ]
    },
    {
      id: '5',
      title: 'Sending a Receipt',
      icon: '🧾',
      steps: [
        {
          step: 1,
          title: 'Complete the Journey',
          description: 'Ensure the job is marked as completed in your diary.',
          details: 'The receipt option is only available for completed jobs.'
        },
        {
          step: 2,
          title: 'Access Receipt Options',
          description: 'Go to the completed job and look for "Send Receipt" or "Generate Receipt".',
          details: 'This option is typically found in the job details or actions menu.'
        },
        {
          step: 3,
          title: 'Enter Fare Details',
          description: 'Fill in the fare information:',
          details: [
            'Total Fare: The amount charged to the customer',
            'Payment Method: Cash, Card, or Bank Transfer',
            'Additional Charges: Any extra fees (waiting time, etc.)',
            'Notes: Any relevant information for the customer'
          ]
        },
        {
          step: 4,
          title: 'Send Receipt',
          description: 'Click "Send Receipt" to email it to the customer.',
          details: 'The receipt will be automatically sent to the customer\'s email address on file.'
        },
        {
          step: 5,
          title: 'Confirm Delivery',
          description: 'Check that the receipt was sent successfully.',
          details: 'You should see a confirmation message, and the receipt will be stored in the system.'
        }
      ]
    },
    {
      id: '6',
      title: 'Completing a Job',
      icon: '✅',
      steps: [
        {
          step: 1,
          title: 'Finish the Journey',
          description: 'Complete the passenger journey to the destination.',
          details: 'Ensure the customer has reached their destination safely.'
        },
        {
          step: 2,
          title: 'Process Payment',
          description: 'Collect payment from the customer using the agreed method.',
          details: 'Accept cash, card payment, or note if it\'s a bank transfer.'
        },
        {
          step: 3,
          title: 'Mark Job as Complete',
          description: 'Go to "My Diary" and find the job in your active jobs.',
          details: 'Look for the "Complete" or "Mark Complete" button for that specific job.'
        },
        {
          step: 4,
          title: 'Enter Completion Details',
          description: 'Fill in the completion form:',
          details: [
            'Actual Fare: The final amount charged',
            'Payment Method: How the customer paid (Cash, Card, or Bank Transfer)',
            'Completion Time: When the journey finished',
            'Notes: Any relevant information about the journey'
          ]
        },
        {
          step: 5,
          title: 'Confirm Completion',
          description: 'Click "Complete Job" to finalize the booking.',
          details: 'The job will move from "Active" to "Completed" status and will appear in your completed jobs history.'
        },
        {
          step: 6,
          title: 'Optional: Send Receipt',
          description: 'If requested, you can now send a receipt to the customer.',
          details: 'Use the "Send Receipt" option to email a formal receipt.'
        }
      ]
    },
    {
      id: '7',
      title: 'Re-assigning Jobs',
      icon: '🔄',
      steps: [
        {
          step: 1,
          title: 'Access Job in My Diary',
          description: 'Go to "My Diary" and find the job you want to reassign.',
          details: 'You can only reassign jobs that you have accepted and are currently managing.'
        },
        {
          step: 2,
          title: 'Click "Re-assign Job"',
          description: 'Look for the "Re-assign Job" button in the job details.',
          details: 'This will open the "Re-assign Job Flash" modal with available options.'
        },
        {
          step: 3,
          title: 'Choose Reassignment Type',
          description: 'Select how you want to reassign the job:',
          details: [
            'Release to Pool: Return the job to the general available jobs pool',
            'Assign to Specific Driver: Transfer the job to another driver'
          ]
        },
        {
          step: 4,
          title: 'Select Target Driver (if applicable)',
          description: 'If assigning to a specific driver, choose from the available drivers list.',
          details: 'The list shows drivers from your company and other companies in the area. You cannot reassign a job to yourself.'
        },
        {
          step: 5,
          title: 'Confirm Reassignment',
          description: 'Review the details and click "Re-assign Job" to confirm.',
          details: 'The job will be transferred and you will no longer be responsible for it.'
        }
      ]
    },
    {
      id: '8',
      title: 'Using Booking Links',
      icon: '🔗',
      steps: [
        {
          step: 1,
          title: 'Access Booking Links',
          description: 'Go to Dashboard → Account → Booking Links in the navigation menu.',
          details: 'This feature allows you to create unique booking links for direct customer bookings.'
        },
        {
          step: 2,
          title: 'Create a New Booking Link',
          description: 'Click "Create New Link" and fill in the details:',
          details: [
            'Link Name: Give your link a memorable name',
            'Maximum Uses: Set how many times the link can be used (1-100)',
            'Pre-fill Data: Optionally pre-fill common pickup/dropoff locations',
            'Notes: Add any special instructions for customers'
          ]
        },
        {
          step: 3,
          title: 'Share Your Booking Link',
          description: 'Copy the generated link and share it with customers.',
          details: 'You can share via email, social media, business cards, or any other method.'
        },
        {
          step: 4,
          title: 'Receive Bookings',
          description: 'When customers use your link, jobs appear in "My Diary" with special indicators.',
          details: 'Look for jobs with a flashing blue border and "🔗 Booking Link" badge.'
        },
        {
          step: 5,
          title: 'Review and Accept Bookings',
          description: 'Review the booking details and accept the job to confirm.',
          details: 'Jobs from booking links start as "pending" and require your acceptance.'
        },
        {
          step: 6,
          title: 'Manage Your Links',
          description: 'Monitor usage, edit settings, or deactivate links as needed.',
          details: 'You can see how many times each link has been used and manage multiple links.'
        }
      ]
    },
    {
      id: '9',
      title: 'Job Monitoring',
      icon: '👀',
      steps: [
        {
          step: 1,
          title: 'Access Job Monitor Settings',
          description: 'Go to Dashboard → Account → Job Monitoring Settings.',
          details: 'This feature alerts you when new jobs become available in your areas.'
        },
        {
          step: 2,
          title: 'Set Postcode Areas',
          description: 'Add the postcode areas you want to monitor for new jobs.',
          details: 'Enter postcode areas like "EX12" or "DT6" to cover those regions.'
        },
        {
          step: 3,
          title: 'Configure Alert Settings',
          description: 'Choose how you want to be notified:',
          details: [
            'Screen Flash: Visual alert on your screen',
            'Sound Alert: Audio notification',
            'Email Alert: Email notifications',
            'Check Interval: How often to check for new jobs (1-30 minutes)',
            'Auto-stop: Stop alerting after a set time period'
          ]
        },
        {
          step: 4,
          title: 'Start Monitoring',
          description: 'Click "Start Monitoring" to begin receiving alerts.',
          details: 'The system will check for new jobs in your specified areas at the set interval.'
        },
        {
          step: 5,
          title: 'Respond to Alerts',
          description: 'When you receive an alert, click to view the new jobs.',
          details: 'You can quickly access available jobs and accept them if interested.'
        },
        {
          step: 6,
          title: 'Manage Monitoring',
          description: 'Pause, resume, or stop monitoring as needed.',
          details: 'You can adjust settings or stop monitoring when you\'re not available.'
        }
      ]
    },
    {
      id: '10',
      title: 'Driver Statistics',
      icon: '📊',
      steps: [
        {
          step: 1,
          title: 'Access Driver Dashboard',
          description: 'Go to Dashboard → Account → Driver Dashboard.',
          details: 'This provides detailed statistics about your job performance and earnings.'
        },
        {
          step: 2,
          title: 'View Monthly Metrics',
          description: 'Review your monthly performance statistics:',
          details: [
            'Total Jobs: Number of jobs accepted this month',
            'Completed Jobs: Number of jobs finished',
            'Cancelled Jobs: Number of jobs cancelled',
            'Average Fare: Average amount per job',
            'Total Earnings: Total income for the month'
          ]
        },
        {
          step: 3,
          title: 'Check Financial Reports',
          description: 'View detailed financial breakdowns and trends.',
          details: 'See earnings by day, week, or month with visual charts and graphs.'
        },
        {
          step: 4,
          title: 'Export Data',
          description: 'Download your statistics for record keeping or tax purposes.',
          details: 'Export data in various formats for your business records.'
        }
      ]
    },
    {
      id: '11',
      title: 'Fare Calculator',
      icon: '💰',
      steps: [
        {
          step: 1,
          title: 'Access Fare Calculator',
          description: 'Click on "Fare Calculator" in the main navigation menu.',
          details: 'This tool helps estimate taxi fares for different journeys.'
        },
        {
          step: 2,
          title: 'Select Council Area',
          description: 'Choose the local council area for the journey.',
          details: 'Different areas have different fare structures and tariffs.'
        },
        {
          step: 3,
          title: 'Enter Journey Details',
          description: 'Fill in the pickup and destination addresses.',
          details: 'Use specific addresses or postcodes for accurate calculations.'
        },
        {
          step: 4,
          title: 'View Fare Estimates',
          description: 'Review the calculated fare estimates for different tariffs.',
          details: 'See distance, duration, and fare breakdown for the journey.'
        },
        {
          step: 5,
          title: 'Book or Get Directions',
          description: 'Use the results to book a taxi or get directions.',
          details: 'You can book directly through the system or open Google Maps for directions.'
        }
      ]
    },
    {
      id: '12',
      title: 'Profile Management',
      icon: '👤',
      steps: [
        {
          step: 1,
          title: 'Access Your Profile',
          description: 'Go to Dashboard → Account → Profile.',
          details: 'Keep your profile information up to date for better job matching.'
        },
        {
          step: 2,
          title: 'Update Personal Information',
          description: 'Maintain current contact details and personal information.',
          details: 'Include your name, phone number, email, and company details.'
        },
        {
          step: 3,
          title: 'Manage Vehicle Information',
          description: 'Keep vehicle details current for licensing requirements.',
          details: 'Update car make, model, registration, and seating capacity.'
        },
        {
          step: 4,
          title: 'Update Licensing Details',
          description: 'Ensure licensing information is accurate and current.',
          details: 'Include authority, badge number, and license type information.'
        },
        {
          step: 5,
          title: 'Set Coverage Areas',
          description: 'Define the postcode areas you cover for work.',
          details: 'This helps match you with relevant jobs in your service areas.'
        },
        {
          step: 6,
          title: 'Upload Visual Assets',
          description: 'Add your logo, car photo, and driver photo.',
          details: 'Professional images help build customer trust and recognition.'
        }
      ]
    },
    {
      id: '13',
      title: 'Listing Only Role',
      icon: '👁️',
      steps: [
        {
          step: 1,
          title: 'Understanding Listing Only Access',
          description: 'If you have "listing_only" role, you can view jobs but not accept them.',
          details: 'This role is for users who need to see available work but cannot perform jobs.'
        },
        {
          step: 2,
          title: 'View Available Jobs',
          description: 'Access the main dashboard to see all available jobs.',
          details: 'You can search, filter, and view job details but cannot take action on them.'
        },
        {
          step: 3,
          title: 'Access Support Features',
          description: 'Use training and troubleshooting sections for help.',
          details: 'You have full access to support resources and can create support tickets.'
        },
        {
          step: 4,
          title: 'Manage Your Profile',
          description: 'Update your account information and preferences.',
          details: 'You can edit personal details, company information, and contact details.'
        },
        {
          step: 5,
          title: 'Job Monitoring (View Only)',
          description: 'Set up job monitoring to see when new jobs become available.',
          details: 'You can monitor postcode areas but cannot accept the jobs you see.'
        }
      ]
    },
    {
      id: '14',
      title: 'Booking Links - Personal Booking System',
      icon: '🔗',
      steps: [
        {
          step: 1,
          title: 'Access Booking Links',
          description: 'Go to Dashboard → Admin → Booking Links.',
          details: 'This feature allows you to create unique booking links for direct customer acquisition.'
        },
        {
          step: 2,
          title: 'Create a New Booking Link',
          description: 'Click "Create New Link" and fill in the details:',
          details: [
            'Link Name: Give your link a memorable name',
            'Usage Limit: Set how many times the link can be used (1-1000)',
            'Pre-fill Data: Set default pickup/destination addresses',
            'Notes: Add any special instructions for customers'
          ]
        },
        {
          step: 3,
          title: 'Share Your Booking Link',
          description: 'Copy the generated link and share it with customers.',
          details: 'Share via WhatsApp, SMS, social media, or business cards. Customers can book directly with you.'
        },
        {
          step: 4,
          title: 'Manage Your Links',
          description: 'Monitor usage, edit settings, or deactivate links as needed.',
          details: 'View usage statistics, edit link details, or delete unused links from your dashboard.'
        },
        {
          step: 5,
          title: 'Receive Bookings',
          description: 'Jobs from booking links appear in your diary with special indicators.',
          details: 'Look for the flashing blue border and "🔗 Booking Link" badge to identify direct bookings.'
        }
      ]
    },
    {
      id: '15',
      title: 'Financial Summary & Analytics',
      icon: '💰',
      steps: [
        {
          step: 1,
          title: 'Access Financial Summary',
          description: 'Go to My Diary → Financial Summary tab.',
          details: 'View comprehensive financial tracking and earnings analysis.'
        },
        {
          step: 2,
          title: 'View Daily/Weekly/Monthly Totals',
          description: 'See your earnings broken down by time periods.',
          details: [
            'Today\'s Earnings: Current day totals',
            'This Week: Weekly summary with daily breakdown',
            'Monthly Reports: Complete monthly performance',
            'Yearly Overview: Annual earnings and trends'
          ]
        },
        {
          step: 3,
          title: 'Drill-Down Analysis',
          description: 'Click on any total amount to see detailed breakdown.',
          details: 'View individual jobs, payment methods, and earnings sources that make up each total.'
        },
        {
          step: 4,
          title: 'Export Financial Reports',
          description: 'Print or email reports for your records.',
          details: 'Generate professional reports for tax purposes, business planning, or record keeping.'
        }
      ]
    },
    {
      id: '16',
      title: 'Email Log & Communication',
      icon: '📧',
      steps: [
        {
          step: 1,
          title: 'Access Email Log',
          description: 'Go to Dashboard → Admin → Email Log.',
          details: 'View all emails sent from your account with complete tracking.'
        },
        {
          step: 2,
          title: 'View Email History',
          description: 'See all emails sent to customers with details.',
          details: [
            'Email Type: Diary, booking confirmation, lead notification, etc.',
            'Recipient: Customer email addresses',
            'Subject: Email subject lines',
            'Status: Sent, failed, or pending',
            'Date & Time: When emails were sent'
          ]
        },
        {
          step: 3,
          title: 'View Full Email Content',
          description: 'Click on any email entry to see the complete email content.',
          details: 'View the full HTML content of emails sent to customers for reference.'
        },
        {
          step: 4,
          title: 'Filter and Search',
          description: 'Use filters to find specific emails.',
          details: 'Filter by email type, status, date range, or search for specific content.'
        }
      ]
    },
    {
      id: '17',
      title: 'Real-Time Job Monitoring',
      icon: '📱',
      steps: [
        {
          step: 1,
          title: 'Enable Job Monitoring',
          description: 'Go to Dashboard → Admin → Job Monitoring Settings.',
          details: 'Configure your job monitoring preferences and notification settings.'
        },
        {
          step: 2,
          title: 'Set Notification Preferences',
          description: 'Choose how you want to be notified of new jobs.',
          details: [
            'Push Notifications: Instant alerts on your device',
            'Audio Alerts: Sound notifications for new jobs',
            'Email Notifications: Email alerts for important updates',
            'SMS Alerts: Text message notifications'
          ]
        },
        {
          step: 3,
          title: 'Monitor Job Status',
          description: 'Track job status changes in real-time.',
          details: 'See when jobs are accepted, completed, or cancelled with instant updates.'
        },
        {
          step: 4,
          title: 'Location Tracking',
          description: 'Enable GPS tracking for better customer service.',
          details: 'Share your location with customers and optimize routes with integrated mapping.'
        }
      ]
    },
    {
      id: '18',
      title: 'Driver Dashboard & Performance',
      icon: '📊',
      steps: [
        {
          step: 1,
          title: 'Access Driver Dashboard',
          description: 'Go to Dashboard → Admin → Driver Dashboard.',
          details: 'View comprehensive performance metrics and business analytics.'
        },
        {
          step: 2,
          title: 'Review Performance Metrics',
          description: 'Analyze your business performance with detailed statistics.',
          details: [
            'Job Statistics: Total jobs, completion rates, averages',
            'Earnings Analysis: Daily, weekly, monthly earnings',
            'Performance Trends: Track your business growth over time',
            'Customer Insights: Understand your customer base'
          ]
        },
        {
          step: 3,
          title: 'Set Business Goals',
          description: 'Use analytics to set and track earnings targets.',
          details: 'Set monthly goals, track progress, and identify opportunities for growth.'
        },
        {
          step: 4,
          title: 'Export Business Reports',
          description: 'Download detailed reports for business planning.',
          details: 'Generate reports for tax purposes, business analysis, or performance reviews.'
        }
      ]
    },
    {
      id: '19',
      title: 'Mobile App Features',
      icon: '📱',
      steps: [
        {
          step: 1,
          title: 'Mobile-First Design',
          description: 'The system is optimized for mobile devices.',
          details: 'Access all features from your phone or tablet with responsive design.'
        },
        {
          step: 2,
          title: 'Touch-Optimized Interface',
          description: 'Easy finger navigation and touch controls.',
          details: 'Large buttons, swipe gestures, and intuitive mobile interface design.'
        },
        {
          step: 3,
          title: 'Offline Capabilities',
          description: 'Work without internet connection when needed.',
          details: 'View your diary, access job details, and work offline with automatic sync when online.'
        },
        {
          step: 4,
          title: 'Push Notifications',
          description: 'Stay connected with instant notifications.',
          details: 'Receive alerts for new jobs, status updates, and important system messages.'
        }
      ]
    }
  ];

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="sticky-top bg-white border-bottom pb-3" style={{ 
        zIndex: 1020,
        top: '56px'
      }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h2 className="mb-0">Driver Training</h2>
            <p className="text-muted mb-0">Step-by-step guides for common tasks and new features</p>
          </div>
          <Badge bg="info" className="fs-6">{trainingGuides.length} Guides Available</Badge>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-lg-8">
          <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
            {trainingGuides.map((guide) => (
              <Accordion.Item key={guide.id} eventKey={guide.id}>
                <Accordion.Header>
                  <div className="d-flex align-items-center">
                    <span className="me-3 fs-4">{guide.icon}</span>
                    <div>
                      <strong>{guide.title}</strong>
                      <div className="text-muted small">
                        {guide.steps.length} steps • Click to expand
                      </div>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="training-steps">
                    {guide.steps.map((step, index) => (
                      <div key={index} className="step-item mb-4">
                        <div className="step-header d-flex align-items-start">
                          <div className="step-number me-3">
                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" 
                                 style={{ width: '40px', height: '40px', fontSize: '1.2rem', fontWeight: 'bold' }}>
                              {step.step}
                            </div>
                          </div>
                          <div className="step-content flex-grow-1">
                            <h5 className="mb-2">{step.title}</h5>
                            <p className="text-muted mb-2">{step.description}</p>
                            <div className="step-details">
                              {Array.isArray(step.details) ? (
                                <ul className="list-unstyled">
                                  {step.details.map((detail, detailIndex) => (
                                    <li key={detailIndex} className="mb-1">
                                      <i className="bi bi-arrow-right text-primary me-2"></i>
                                      {detail}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-muted small mb-0">{step.details}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>

        <div className="col-lg-4">
          <Card className="sticky-top" style={{ top: '120px' }}>
            <Card.Header>
              <h5 className="mb-0">Quick Navigation</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-grid gap-2">
                {trainingGuides.map((guide) => (
                  <Button
                    key={guide.id}
                    variant={activeKey === guide.id ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => setActiveKey(guide.id)}
                    className="text-start"
                  >
                    <span className="me-2">{guide.icon}</span>
                    {guide.title}
                  </Button>
                ))}
              </div>
              
              <hr />
              
              <div className="text-center">
                <h6>Need More Help?</h6>
                <p className="text-muted small">
                  If you need additional support, please contact the admin team or check the troubleshooting section.
                </p>
                <Button variant="outline-secondary" size="sm">
                  Contact Support
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      <style jsx>{`
        .training-steps .step-item:not(:last-child) {
          border-bottom: 1px solid #e9ecef;
          padding-bottom: 1.5rem;
        }
        
        .step-content h5 {
          color: #495057;
          font-weight: 600;
        }
        
        .step-details ul li {
          padding: 0.25rem 0;
          border-left: 2px solid #e9ecef;
          padding-left: 1rem;
          margin-left: 0.5rem;
        }
        
        .step-details ul li:last-child {
          border-left: none;
        }
      `}</style>
    </div>
  );
} 